// Input Components - Cosmic Glassmorphism Design
import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';

// Text Input
export const Input = forwardRef(({
    label,
    type = 'text',
    error,
    className = '',
    ...props
}, ref) => {
    return (
        <div className="w-full">
            {label && (
                <label className="block text-sm font-medium text-amber-200 mb-2">
                    {label}
                </label>
            )}
            <motion.input
                ref={ref}
                type={type}
                className={`
                    w-full px-4 py-3
                    bg-white/5 backdrop-blur-md
                    border border-white/10 
                    focus:border-amber-500/50 focus:bg-white/10
                    rounded-xl
                    text-amber-100 placeholder-gray-500
                    transition-all duration-300
                    focus:outline-none focus:ring-2 focus:ring-amber-500/20
                    ${error ? 'border-red-500/50' : ''}
                    ${className}
                `}
                whileFocus={{ scale: 1.01 }}
                {...props}
            />
            {error && (
                <p className="mt-1.5 text-sm text-red-400">{error}</p>
            )}
        </div>
    );
});

Input.displayName = 'Input';

// Textarea
export const Textarea = forwardRef(({
    label,
    error,
    showCount = false,
    maxLength,
    value = '',
    className = '',
    ...props
}, ref) => {
    const charCount = value.length;
    const isNearLimit = maxLength && charCount > maxLength * 0.8;
    const isAtLimit = maxLength && charCount >= maxLength;

    return (
        <div className="w-full">
            {label && (
                <label className="block text-sm font-medium text-amber-200 mb-2">
                    {label}
                </label>
            )}
            <div className="relative">
                <motion.textarea
                    ref={ref}
                    value={value}
                    maxLength={maxLength}
                    className={`
                        w-full px-4 py-3
                        bg-white/5 backdrop-blur-md
                        border border-white/10 
                        focus:border-amber-500/50 focus:bg-white/10
                        rounded-xl
                        text-amber-100 placeholder-gray-500
                        transition-all duration-300
                        focus:outline-none focus:ring-2 focus:ring-amber-500/20
                        resize-none
                        ${error ? 'border-red-500/50' : ''}
                        ${className}
                    `}
                    whileFocus={{ scale: 1.005 }}
                    {...props}
                />

                {/* Glow effect on focus */}
                <div className="absolute inset-0 rounded-xl pointer-events-none opacity-0 focus-within:opacity-100 transition-opacity">
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-amber-500/10 via-transparent to-amber-500/10" />
                </div>
            </div>

            {/* Character count */}
            {showCount && maxLength && (
                <div className="flex justify-end mt-1.5">
                    <span className={`text-xs transition-colors ${isAtLimit ? 'text-red-400' :
                            isNearLimit ? 'text-amber-400' :
                                'text-gray-500'
                        }`}>
                        {charCount} / {maxLength}
                    </span>
                </div>
            )}

            {error && (
                <p className="mt-1.5 text-sm text-red-400">{error}</p>
            )}
        </div>
    );
});

Textarea.displayName = 'Textarea';

// Search Input with icon
export const SearchInput = forwardRef(({
    placeholder = 'Search...',
    className = '',
    ...props
}, ref) => {
    return (
        <div className="relative">
            <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
            </svg>
            <input
                ref={ref}
                type="text"
                placeholder={placeholder}
                className={`
                    w-full pl-10 pr-4 py-2.5
                    bg-white/5 backdrop-blur-md
                    border border-white/10 
                    focus:border-amber-500/50 focus:bg-white/10
                    rounded-xl
                    text-amber-100 placeholder-gray-500
                    transition-all duration-300
                    focus:outline-none focus:ring-2 focus:ring-amber-500/20
                    ${className}
                `}
                {...props}
            />
        </div>
    );
});

SearchInput.displayName = 'SearchInput';

export default Input;
